import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { RootState } from '..';
import { MedicalLeaveRequestBaseResponseDto, RoleTypeEnum } from '../../commonTypes/user.types';
import { UserOrganizationPolicyDto, UserProfileExtendedDto } from '../../commonTypes/acs/user.types';
import { restoreAll } from '../restoreAll';
import { LeaveRequestDto, LeaveRequestStatus, LeaveRequestType } from '../../commonTypes/leaveRequest.types';

export interface UserProfileState {
    value: UserProfileExtendedDto & {
        leaveRequestsHistory: LeaveRequestDto[];
        upcomingLeaveRequests: LeaveRequestDto[];
        upcomingMedicalLeaveRequests: MedicalLeaveRequestBaseResponseDto[];
        medicalLeaveRequestsHistory: MedicalLeaveRequestBaseResponseDto[];
        leaveRequestHistoryForExport: LeaveRequestDto[];
    };
    leaveRequestActionStatuses: {
        [key: string]: LeaveRequestStatus;
    };
    medicalLeaveRequestActionStatuses: {
        [key: string]: LeaveRequestStatus;
    };
}

interface IEditUpcomingLeaveRequest {
    id: string;
    newStatus: LeaveRequestStatus;
    rejectReason?: string;
}

const initialState: UserProfileState = {
    value: {
        userSub: '',
        email: '',
        organizationId: '',
        branchId: '',
        joinedAt: '',
        contractEndDate: '',
        profile: {
            userSub: '',
            givenName: '',
            familyName: '',
            children: [],
            imageUrl: '',
            updatedAt: '',
        },
        role: {
            name: RoleTypeEnum.BASIC,
        },
        projects: [],
        userOrganizationPolicies: [],
        leaveRequestsHistory: [],
        upcomingLeaveRequests: [],
        leaveRequestHistoryForExport: [],
        upcomingMedicalLeaveRequests: [],
        medicalLeaveRequestsHistory: [],
    },
    leaveRequestActionStatuses: {},
    medicalLeaveRequestActionStatuses: {},
};

const UserProfileStateSlice = createSlice({
    name: 'userProfile',
    initialState,
    extraReducers: builder => builder.addCase(restoreAll, () => initialState),
    reducers: {
        addUserProfile: (state, action) => {
            action.payload.userOrganizationPolicies.forEach((policy: UserOrganizationPolicyDto, index: number) => {
                if (policy.leaveType.toLowerCase() === LeaveRequestType.VACATION.toLowerCase()) {
                    action.payload.userOrganizationPolicies.splice(index, 1);
                    action.payload.userOrganizationPolicies.unshift(policy);
                }
            });

            state.value = action.payload;
        },
        addProfileLeaveRequestHistory: (state, action) => {
            state.value.leaveRequestsHistory = action.payload;
        },
        addProfileUpcomingLeaveRequests: (state, action) => {
            state.value.upcomingLeaveRequests = action.payload;
        },
        editUserProfile: (state, action) => {
            state.value.profile = { ...state.value.profile, ...action.payload };
        },
        editUserRole: (state, action: PayloadAction<RoleTypeEnum>) => {
            state.value.role.name = action.payload;
        },
        editUserProfileImage: (state, action) => {
            state.value.profile.imageUrl = action.payload;
        },
        editUserEmergencyContact: (state, action) => {
            state.value.profile.emergencyContact = action.payload;
        },
        editUpcomingLeaveRequest: (state, action: PayloadAction<IEditUpcomingLeaveRequest>) => {
            state.value.upcomingLeaveRequests = state.value.upcomingLeaveRequests.map(req => {
                if (req.id === action.payload.id) {
                    req.status = action.payload.newStatus;
                    req.rejectReason = action.payload.rejectReason;
                }
                return req;
            });

            state.value.upcomingMedicalLeaveRequests = state.value.upcomingMedicalLeaveRequests.map(req => {
                if (req.id === action.payload.id) {
                    req.status = action.payload.newStatus;
                }
                return req;
            });

            state.leaveRequestActionStatuses[action.payload.id] = action.payload.newStatus;
            state.medicalLeaveRequestActionStatuses[action.payload.id] = action.payload.newStatus;
        },
        editHistoryMedicalLeaveRequest: (
            state,
            action: PayloadAction<{
                medicalLeaveRequest: MedicalLeaveRequestBaseResponseDto;
                newStatus: LeaveRequestStatus;
                rejectReason?: string;
            }>
        ) => {
            const { medicalLeaveRequest, newStatus, rejectReason } = action.payload;

            state.value.upcomingMedicalLeaveRequests = state.value.upcomingMedicalLeaveRequests.filter(req => req.id !== medicalLeaveRequest.id);

            const existing = state.value.medicalLeaveRequestsHistory.find(req => req.id === medicalLeaveRequest.id);

            if (existing) {
                existing.status = newStatus;
                if (rejectReason) {
                    (existing as any).rejectReason = rejectReason;
                }
            } else {
                state.value.medicalLeaveRequestsHistory.push({
                    ...medicalLeaveRequest,
                    status: newStatus,
                    ...(rejectReason ? { rejectReason } : {}),
                });
            }

            state.medicalLeaveRequestActionStatuses[medicalLeaveRequest.id] = newStatus;
        },

        editStartEndDate: (state, action) => {
            state.value.joinedAt = action.payload.joinedAt;
            state.value.contractEndDate = action.payload.contractEndDate;
        },
        addProfileLeaveRequestHistoryForExport: (state, action) => {
            state.value.leaveRequestHistoryForExport = action.payload.items || action.payload;
        },
        updateLeaveRequestActionStatus: (state, action: PayloadAction<{ leaveRequestId: string; status: LeaveRequestStatus }>) => {
            state.leaveRequestActionStatuses[action.payload.leaveRequestId] = action.payload.status;
        },
        updateMedicalLeaveRequestActionStatus: (state, action: PayloadAction<{ medicalLeaveRequestId: string; status: LeaveRequestStatus }>) => {
            state.medicalLeaveRequestActionStatuses[action.payload.medicalLeaveRequestId] = action.payload.status;
        },
        addProfileUpcomingMedicalLeaveRequests: (state, action) => {
            state.value.upcomingMedicalLeaveRequests = action.payload;
        },
        addProfileMedicalLeaveRequestHistory: (state, action) => {
            state.value.medicalLeaveRequestsHistory = action.payload;
        },
    },
});

export const {
    addUserProfile,
    editUserProfile,
    editUserRole,
    editUserProfileImage,
    editUserEmergencyContact,
    addProfileLeaveRequestHistory,
    addProfileUpcomingLeaveRequests,
    editUpcomingLeaveRequest,
    editHistoryMedicalLeaveRequest,
    editStartEndDate,
    addProfileLeaveRequestHistoryForExport,
    updateLeaveRequestActionStatus,
    updateMedicalLeaveRequestActionStatus,
    addProfileUpcomingMedicalLeaveRequests,
    addProfileMedicalLeaveRequestHistory,
} = UserProfileStateSlice.actions;

export const selectUserProfile = (state: RootState) => state.userProfile.value;

export const selectProfileProjects = (state: RootState) => state.userProfile.value.projects;

// Enriched selector that adds projectStatus from cached projects
export const selectProfileProjectsWithStatus = (state: RootState) => {
    const profileProjects = state.userProfile.value.projects;
    const cachedProjects = state.projects.cachedProjects;

    // If no cached projects, return profile projects as-is
    if (!cachedProjects || cachedProjects.length === 0) {
        return profileProjects;
    }

    // Create a map of project IDs to their status
    const projectStatusMap = new Map(cachedProjects.map(p => [p.id, p.projectStatus]));

    // Enrich profile projects with status from cached projects
    return profileProjects.map(profileProject => ({
        ...profileProject,
        projectStatus: projectStatusMap.get(profileProject.id) || profileProject.projectStatus,
    }));
};

export const selectProfilePolicies = (state: RootState) => state.userProfile.value.userOrganizationPolicies;

export const selectProfileUserSub = (state: RootState) => state.userProfile.value.userSub;

export const selectUpcomingRequests = (state: RootState) => state.userProfile.value.upcomingLeaveRequests;

export const selectHistoryRequests = (state: RootState) => state.userProfile.value.leaveRequestsHistory;

export const selectHistoryRequestsForExport = (state: RootState) => state.userProfile.value.leaveRequestHistoryForExport;

export const selectLeaveRequestActionStatuses = (state: RootState) => state.userProfile.leaveRequestActionStatuses;

export const selectMedicalLeaveRequestActionStatuses = (state: RootState) => state.userProfile.medicalLeaveRequestActionStatuses;

export const selectUpcomingMedicalRequests = (state: RootState) => state.userProfile.value.upcomingMedicalLeaveRequests;

export const selectHistoryMedicalRequests = (state: RootState) => state.userProfile.value.medicalLeaveRequestsHistory;

export default UserProfileStateSlice.reducer;
